<script setup>
// Diagnóstico do contrato: conexão, sinal óptico, sessão e dados do cliente.
// Porta da AbaSuporte do eryx-sgp.
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import messages from './i18n';
import { erroDaApi } from './api';
import { bytes, duracao, momento } from './format';
import SgpSelo from './SgpSelo.vue';

const props = defineProps({
  api: { type: Object, required: true },
});

const { t } = useI18n({ messages });

const servicos = ref(null);
const carregando = ref(false);
const erro = ref('');
const copiado = ref('');

const busca = async () => {
  carregando.value = true;
  erro.value = '';
  try {
    const { data } = await props.api.servicos();
    servicos.value = data.servicos;
  } catch (error) {
    erro.value = erroDaApi(error, t('NEUXS_SGP.ERRO_GENERICO'));
  } finally {
    carregando.value = false;
  }
};

const copia = async (texto, chave) => {
  await navigator.clipboard.writeText(texto);
  copiado.value = chave;
  setTimeout(() => {
    if (copiado.value === chave) copiado.value = '';
  }, 2000);
};

// Linhas de cada bloco, na ordem da tela. `copiar` marca o que o técnico cola
// em outro sistema.
const conexao = s => [
  { rotulo: 'LOGIN', valor: s.login, copiar: true, mono: true },
  { rotulo: 'IP', valor: s.ip, copiar: true, mono: true },
  { rotulo: 'IPV6', valor: s.ipv6, mono: true },
  { rotulo: 'MAC', valor: s.mac, copiar: true, mono: true },
  { rotulo: 'PLANO', valor: s.plano },
  { rotulo: 'NAS', valor: s.nas_ip, mono: true },
  { rotulo: 'PORTA', valor: s.porta, mono: true },
];
const onu = s => [
  { rotulo: 'SERIAL', valor: s.onu.serial, copiar: true, mono: true },
  {
    rotulo: 'OLT',
    valor: [s.onu.olt, s.onu.slot, s.onu.pon].filter(Boolean).join(' / '),
    mono: true,
  },
  { rotulo: 'MEDIDO', valor: s.onu.medido_em && momento(s.onu.medido_em) },
];
const contrato = s => [
  { rotulo: 'SITUACAO', valor: s.status_em },
  { rotulo: 'CADASTRO', valor: s.cadastro },
  { rotulo: 'POP', valor: s.pop },
];
const cliente = s => [
  { rotulo: 'NOME', valor: s.cliente },
  ...s.contatos.map(c => ({ rotulo: 'TELEFONE', valor: c, copiar: true })),
  { rotulo: 'EMAIL', valor: s.email },
  { rotulo: 'LOCAL', valor: s.endereco },
  { rotulo: 'CIDADE', valor: [s.cidade, s.uf].filter(Boolean).join('/') },
  { rotulo: 'REFERENCIA', valor: s.referencia },
  { rotulo: 'COORDENADAS', valor: s.coordenadas, copiar: true, mono: true },
];

// Bloco sem nenhum dado não aparece: um título solto não diz nada.
const blocos = s =>
  [
    { titulo: t('NEUXS_SGP.SUPORTE.CONEXAO'), linhas: conexao(s) },
    {
      titulo: t('NEUXS_SGP.SUPORTE.CONTRATO', { id: s.contrato_id }),
      linhas: contrato(s),
      selo: s.status && { tom: s.tom_status, texto: s.status },
    },
    { titulo: t('NEUXS_SGP.SUPORTE.CLIENTE'), linhas: cliente(s) },
  ].filter(bloco => bloco.selo || bloco.linhas.some(linha => linha.valor));

onMounted(busca);
</script>

<template>
  <div
    v-if="carregando && !servicos"
    class="flex items-center gap-2 text-n-slate-11"
  >
    <Spinner :size="14" />
    {{ t('NEUXS_SGP.CONSULTANDO') }}
  </div>

  <div v-else-if="!servicos" class="flex flex-col items-start gap-2">
    <p class="text-n-ruby-11">{{ erro }}</p>
    <Button
      :label="t('NEUXS_SGP.TENTAR_DE_NOVO')"
      size="xs"
      variant="ghost"
      icon="i-lucide-refresh-cw"
      @click="busca"
    />
  </div>

  <p v-else-if="servicos.length === 0" class="text-n-slate-11">
    {{ t('NEUXS_SGP.SUPORTE.NENHUM') }}
  </p>

  <div v-else class="flex flex-col gap-3">
    <div class="flex justify-end">
      <Button
        icon="i-lucide-refresh-cw"
        size="xs"
        variant="ghost"
        color="slate"
        :is-loading="carregando"
        :title="t('NEUXS_SGP.ATUALIZAR')"
        @click="busca"
      />
    </div>
    <p v-if="erro" class="text-n-ruby-11">{{ erro }}</p>

    <div
      v-for="(s, i) in servicos"
      :key="s.id || i"
      class="flex flex-col gap-3 p-2 rounded-lg outline outline-1 outline-n-weak"
    >
      <div
        class="flex flex-wrap items-center gap-x-2 gap-y-1 p-2 rounded-lg"
        :class="
          s.online ? 'bg-n-teal-3 text-n-teal-11' : 'bg-n-ruby-3 text-n-ruby-11'
        "
      >
        <span :class="s.online ? 'i-lucide-wifi' : 'i-lucide-wifi-off'" />
        <strong>
          {{
            s.online
              ? t('NEUXS_SGP.SUPORTE.CONECTADO')
              : t('NEUXS_SGP.SUPORTE.SEM_CONEXAO')
          }}
        </strong>
        <span v-if="s.online && s.tempo_segundos > 0" class="text-xs">
          {{ t('NEUXS_SGP.SUPORTE.HA', { tempo: duracao(s.tempo_segundos) }) }}
        </span>
        <span v-if="s.desde" class="text-xs">
          {{ t('NEUXS_SGP.SUPORTE.DESDE', { momento: momento(s.desde) }) }}
        </span>
      </div>

      <!-- Sinal: o número do diagnóstico. Ausente não é zero dBm. -->
      <div class="flex flex-col gap-2">
        <h4 class="text-xs font-medium text-n-slate-11">
          {{ t('NEUXS_SGP.SUPORTE.SINAL') }}
        </h4>
        <div class="grid grid-cols-2 gap-2">
          <div
            v-for="leitura in [
              { dbm: s.onu.rx, tom: s.onu.tom_rx, rotulo: 'ONU_RECEBE' },
              {
                dbm: s.onu.olt_rx,
                tom: s.onu.tom_olt_rx,
                rotulo: 'OLT_RECEBE',
              },
            ]"
            :key="leitura.rotulo"
            class="flex flex-col items-start gap-1 p-2 rounded-lg bg-n-alpha-2"
          >
            <strong>{{
              leitura.dbm === null ? '—' : `${leitura.dbm} dBm`
            }}</strong>
            <span class="text-xs text-n-slate-11">
              {{ t(`NEUXS_SGP.SUPORTE.${leitura.rotulo}`) }}
            </span>
            <SgpSelo
              :tom="leitura.tom"
              :texto="t(`NEUXS_SGP.SUPORTE.TOM.${leitura.tom}`)"
            />
          </div>
        </div>
        <template v-for="linha in onu(s)" :key="linha.rotulo">
          <div v-if="linha.valor" class="flex items-center gap-2 text-xs">
            <span class="w-24 shrink-0 text-n-slate-11">
              {{ t(`NEUXS_SGP.SUPORTE.CAMPOS.${linha.rotulo}`) }}
            </span>
            <span class="truncate" :class="{ 'font-mono': linha.mono }">
              {{ linha.valor }}
            </span>
            <Button
              v-if="linha.copiar"
              :icon="
                copiado === `${i}-${linha.rotulo}`
                  ? 'i-lucide-check'
                  : 'i-lucide-copy'
              "
              size="xs"
              variant="ghost"
              color="slate"
              :title="t('NEUXS_SGP.COPIAR')"
              @click="copia(linha.valor, `${i}-${linha.rotulo}`)"
            />
          </div>
        </template>
      </div>

      <!-- Tráfego do ponto de vista do cliente: baixado é o que ele recebeu. -->
      <div
        v-if="s.baixado > 0 || s.enviado > 0"
        class="flex gap-3 text-xs text-n-slate-11"
      >
        <span>
          <span class="i-lucide-arrow-down" />
          {{ bytes(s.baixado) }}
        </span>
        <span>
          <span class="i-lucide-arrow-up" />
          {{ bytes(s.enviado) }}
        </span>
        <span>{{ t('NEUXS_SGP.SUPORTE.NESTA_SESSAO') }}</span>
      </div>

      <div
        v-for="bloco in blocos(s)"
        :key="bloco.titulo"
        class="flex flex-col gap-1"
      >
        <h4 class="flex items-center gap-2 text-xs font-medium text-n-slate-11">
          {{ bloco.titulo }}
          <SgpSelo
            v-if="bloco.selo"
            :tom="bloco.selo.tom"
            :texto="bloco.selo.texto"
          />
        </h4>
        <template
          v-for="(linha, j) in bloco.linhas"
          :key="`${linha.rotulo}-${j}`"
        >
          <div v-if="linha.valor" class="flex items-center gap-2 text-xs">
            <span class="w-24 shrink-0 text-n-slate-11">
              {{ t(`NEUXS_SGP.SUPORTE.CAMPOS.${linha.rotulo}`) }}
            </span>
            <span class="truncate" :class="{ 'font-mono': linha.mono }">
              {{ linha.valor }}
            </span>
            <Button
              v-if="linha.copiar"
              :icon="
                copiado === `${i}-${linha.rotulo}-${j}`
                  ? 'i-lucide-check'
                  : 'i-lucide-copy'
              "
              size="xs"
              variant="ghost"
              color="slate"
              :title="t('NEUXS_SGP.COPIAR')"
              @click="copia(linha.valor, `${i}-${linha.rotulo}-${j}`)"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
